// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import React, { ReactElement } from 'react'
import { ReserveActionPlanClass, ReservePlanModeEnum } from './ReserveActionPlanClass'

import Tooltip from '@mui/material/Tooltip'
import { LytReservesClass } from './LytReservesClass'
import { useOtChainState } from '@react/redux/store'
import { LytReservesWithWithdrawalsStateCTO } from '@shared/ReservePlanCTO'
import { logOT } from '@shared/loggingShared'

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function LytIssuerReserveTargetsView(props: {
  reserveActionPlanClass: ReserveActionPlanClass
}): React.ReactElement {
  const { reserveActionPlanClass } = props

  const { reservesWithStats } = reserveActionPlanClass

  const reservesSortedByPriority =
    reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode
      ? reservesWithStats.sort((a, b) => a.depositPriority - b.depositPriority)
      : reservesWithStats.sort((a, b) => a.withdrawPriority - b.withdrawPriority)

  return (
    <Paper sx={{ padding: 3, border: '1px solid', borderColor: 'grey.200' }}>
      <Box data-testid="lyt-issuer-reserve-targets">
        <Box
          sx={{
            overflowX: 'scroll',
            width: '100%',
            '&::-webkit-scrollbar': {
              height: '12px',
              display: 'block',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
            scrollbarWidth: 'auto',
            scrollbarColor: '#c1c1c1 #f1f1f1',
          }}
        >
          <Table key="table" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ paddingX: 2 }}>
                  <Typography variant="caption">Pool Name</Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 2, textAlign: 'center', width: 250 }}>
                  <Typography variant="caption" sx={{ textAlign: 'right' }}>
                    Assets
                  </Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 2, width: 80 }}>
                  <Typography variant="caption">Actual Reserves</Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 2 }}>
                  <Typography variant="caption">APY</Typography>
                </TableCell>
                <TableCell sx={{ paddingX: 1, width: 140, textAlign: 'center' }}>
                  <Tooltip title="Minimum and maximum percentage of reserves to maintain in this pool">
                    <Typography variant="caption">Reserves Range (Min-Max %)</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ paddingX: 1, maxWidth: 100 }}>
                  <Tooltip title="Priority for deposits (1 = highest priority)">
                    <Typography variant="caption">Deposit Priority</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ paddingX: 1, maxWidth: 100 }}>
                  <Tooltip title="Priority for withdrawals (1 = highest priority)">
                    <Typography variant="caption">Withdraw Priority</Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{reservesSortedByPriority.map((reserve) => reserveRow(reserve))}</TableBody>
          </Table>
        </Box>
      </Box>
    </Paper>
  )
}
function liquidityBufferEquationCell(
  reserve: LytReservesClass,
  reserveCTO: LytReservesWithWithdrawalsStateCTO
): ReactElement {
  logOT.debug('liquidityBufferEquationCell rerender 2.1', reserveCTO.assetBalance)
  const actualDepositAmount = reserve.actualDepositAmount
  const reserveActionPlanClass = reserve.reserveActionPlanClass
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.assetBalance)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'(Reserve Deposits) -' + erc20WeiToCommaDecString(BigInt(reserveCTO.assetBalance) - actualDepositAmount)}
        </Typography>

        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'_______'}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.NeutralMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.assetBalance)}
        </Typography>
      </Box>
    )
  }
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.assetBalance)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right', flexDirection: 'row' }}>
          {'(To Be Outflow) - ' + erc20WeiToCommaDecString(reserve.actualWithdrawAmount ?? 0n)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'_______'}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }

  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.OutflowMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.assetBalance)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right', flexDirection: 'row' }}>
          {'(Transferring To Pool) - ' +
            erc20WeiToCommaDecString(
              BigInt(reserveCTO.assetBalance ?? 0n) > BigInt(reserve.postAssetBalance ?? 0n)
                ? BigInt(reserveCTO.assetBalance ?? 0n) - BigInt(reserve.postAssetBalance ?? 0n)
                : 0n
            )}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'_______'}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        NOT IMPLEMENTED
      </Typography>
    </Box>
  )
}

function reserveEquationCell(reserve: LytReservesClass, reserveCTO: LytReservesWithWithdrawalsStateCTO): ReactElement {
  const reserveActionPlanClass = reserve.reserveActionPlanClass
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.InflowMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.currentAssetValue)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'(Deposits)+' + erc20WeiToCommaDecString(reserve.actualDepositAmount)}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'_______'}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.NeutralMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.currentAssetValue)}
        </Typography>
      </Box>
    )
  }
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.RetrieveMode) {
    const pendingRetrieve = reserve.preWithdrawRequestedOrAcceptedAmount - reserve.actualWithdrawAmount
    const usePendingRetrieve = pendingRetrieve > 0n
    logOT.debug('pendingRetrieve', reserve)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserveCTO.currentAssetValue)}
        </Typography>

        {!usePendingRetrieve && (
          <Typography variant="caption" sx={{ textAlign: 'right' }}>
            {'(Retrieve) -' + erc20WeiToCommaDecString(reserve.actualWithdrawAmount ?? 0n)}
          </Typography>
        )}
        {usePendingRetrieve && (
          <Typography variant="caption" sx={{ textAlign: 'right' }}>
            {'(Pending Retrieve) - ' + erc20WeiToCommaDecString(pendingRetrieve)}
          </Typography>
        )}
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {'_______'}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }
  if (reserveActionPlanClass.reservePlanMode === ReservePlanModeEnum.OutflowMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
        <Typography variant="caption" sx={{ textAlign: 'right' }}>
          {erc20WeiToCommaDecString(reserve.postAssetBalance)}
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {erc20WeiToCommaDecString(reserveCTO.currentAssetValue)}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {'(Deposits)+' + erc20WeiToCommaDecString(reserve.actualDepositAmount)}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {'(Withdrawals)-' + erc20WeiToCommaDecString(reserve.actualWithdrawAmount ?? 0n)}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {'_______'}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {erc20WeiToCommaDecString(reserve.postAssetBalance)}
      </Typography>
    </Box>
  )
}

function reserveRow(reserve: LytReservesClass): ReactElement {
  const {
    stageConfiguration: { isLyt },
  } = useOtChainState()

  logOT.debug('liquidityBufferEquationCell rerender 2.0')
  const reserveCTO = reserve.lytReservesCTO
  return (
    <TableRow key={reserveCTO.id}>
      <TableCell sx={{ paddingX: 2 }}>
        <Typography variant="caption">{reserveCTO.poolName}</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 2 }}>
        {reserve.isLiquidityAsset && liquidityBufferEquationCell(reserve, reserveCTO)}
        {!reserve.isLiquidityAsset && reserveEquationCell(reserve, reserveCTO)}
      </TableCell>
      <TableCell sx={{ paddingX: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption">{(reserve?.prePercentReserves ?? 0).toFixed(2)}%</Typography>
          <Typography variant="caption">{' ↓↓↓ '}</Typography>{' '}
          <Typography variant="caption">{(reserve?.postPercentReserves ?? 0).toFixed(2)}%</Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ paddingX: 2 }}>
        <Typography variant="caption">{(parseFloat(reserveCTO.apy) / 100).toFixed(2)}%</Typography>
      </TableCell>
      <TableCell sx={{ paddingX: 1, width: 140 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            padding: 1,
            cursor: isLyt ? 'pointer' : 'default',
            '&:hover': isLyt ? { backgroundColor: 'action.hover', borderRadius: 1 } : {},
            minHeight: 48,
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption">
            Min: {(reserve.lytReservesCTO.percentReservesMin / 100).toString()}%
          </Typography>
          <Typography variant="caption">
            Max: {(reserve.lytReservesCTO.percentReservesMax / 100).toString()}%
          </Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ paddingX: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isLyt ? 'pointer' : 'default',
            '&:hover': isLyt ? { backgroundColor: 'action.hover', borderRadius: 1, padding: 0.5 } : {},
          }}
        >
          <Typography variant="caption">{reserve.lytReservesCTO.depositPriority}</Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ paddingX: 1 }}>
        {' '}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isLyt ? 'pointer' : 'default',
            '&:hover': isLyt ? { backgroundColor: 'action.hover', borderRadius: 1, padding: 0.5 } : {},
          }}
        >
          <Typography variant="caption">{reserve.lytReservesCTO.withdrawalPriority}</Typography>
        </Box>
      </TableCell>
    </TableRow>
  )
}
